import { Select, SelectOption } from "@msys/ui";
import { SelectChangeEvent } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { isEqual } from "lodash-es";
import React from "react";
import { QuotePdfCalculationType } from "../../../../clients/graphqlTypes.js";
import { PdfDownloadArgs } from "../hooks/useDownloadQuotePdf.js";
import { useDownloadQuotePdfOptions } from "../hooks/useDownloadQuotePdfOptions.js";
import { SelectQuotePdf_QuoteFragment } from "./SelectQuotePdf.generated.js";

export interface SelectQuotePdfProps {
  doc: SelectQuotePdf_QuoteFragment;
  value: PdfDownloadArgs;
  onChange: (value: PdfDownloadArgs) => void;
  calculationType?: QuotePdfCalculationType;
  label: string;
  fullWidth?: boolean;
  size?: "small" | "medium" | "extra-small";
}

export const SelectQuotePdf = ({
  doc,
  value,
  onChange,
  calculationType,
  label,
  fullWidth = true,
  size = "small",
}: SelectQuotePdfProps) => {
  const { t } = useTranslate(["Quote"]);

  const {
    hasContractingTemplates,
    hasPdgGeneratorTemplates,
    hasSexyQuotePdfTemplates,
    hasSelectedCalculations,
    contractingPdfs,
    sexyQuotePdfTemplates,
    pdfGeneratorTemplates,
    loading,
  } = useDownloadQuotePdfOptions({ doc });

  // Build options array
  const options: (SelectOption<PdfDownloadArgs> | { groupTitle: string })[] =
    React.useMemo(() => {
      const opts: (SelectOption<PdfDownloadArgs> | { groupTitle: string })[] =
        [];

      // Quote PDF options
      if (
        hasSelectedCalculations &&
        (!calculationType || calculationType === "agreed")
      ) {
        opts.push({
          value: {
            templateType: "none",
            calculationType: "agreed",
          },
          label: `${t("Quote PDF")} (${t("agreed")})`,
          key: "none-agreed",
        });
      }
      if (!calculationType || calculationType === "proposed") {
        opts.push({
          value: {
            templateType: "none",
            calculationType: "proposed",
          },
          label: hasSelectedCalculations
            ? `${t("Quote PDF")} (${t("proposed")})`
            : t("Quote PDF"),
          key: "none-proposed",
        });
      }

      // Contracting PDF options
      if (hasContractingTemplates) {
        const contractingOpts: SelectOption<PdfDownloadArgs>[] = [];
        contractingPdfs.forEach(contractingPdf => {
          if (
            hasSelectedCalculations &&
            (!calculationType || calculationType === "agreed")
          ) {
            contractingOpts.push({
              value: {
                templateType: "contracting",
                calculationType: "agreed",
                contractingPdfId: contractingPdf.id,
              },
              label: `${contractingPdf.title} (${t("agreed")})`,
              key: `contracting-agreed-${contractingPdf.id}`,
            });
          }
          if (!calculationType || calculationType === "proposed") {
            contractingOpts.push({
              value: {
                templateType: "contracting",
                calculationType: "proposed",
                contractingPdfId: contractingPdf.id,
              },
              label: hasSelectedCalculations
                ? `${contractingPdf.title} (${t("proposed")})`
                : contractingPdf.title,
              key: `contracting-proposed-${contractingPdf.id}`,
            });
          }
        });
        if (contractingOpts.length > 0) {
          opts.push({ groupTitle: t("Contracting PDF") });
          opts.push(...contractingOpts);
        }
      }

      // Sexy PDF options (only support "proposed" calculation type)
      if (
        hasSexyQuotePdfTemplates &&
        (!calculationType || calculationType === "proposed")
      ) {
        opts.push({ groupTitle: t("Sexy PDF") });
        sexyQuotePdfTemplates.forEach(pdfTemplate => {
          opts.push({
            value: {
              templateType: "sexy",
              calculationType: "proposed",
              pdfTemplateUrl: pdfTemplate.pdfTemplateUrl,
            },
            label: pdfTemplate.label,
            key: `sexy-proposed-${pdfTemplate.pdfTemplateUrl}`,
          });
        });
      }

      // Generator PDF options
      if (hasPdgGeneratorTemplates) {
        const generatorOpts: SelectOption<PdfDownloadArgs>[] = [];
        pdfGeneratorTemplates.forEach(template => {
          if (
            hasSelectedCalculations &&
            (!calculationType || calculationType === "agreed")
          ) {
            generatorOpts.push({
              value: {
                templateType: "generator",
                calculationType: "agreed",
                pdfGeneratorTemplateId: template.id,
              },
              label: `${template.name} (${t("agreed")})`,
              key: `generator-agreed-${template.id}`,
            });
          }
          if (!calculationType || calculationType === "proposed") {
            generatorOpts.push({
              value: {
                templateType: "generator",
                calculationType: "proposed",
                pdfGeneratorTemplateId: template.id,
              },
              label: hasSelectedCalculations
                ? `${template.name} (${t("proposed")})`
                : template.name,
              key: `generator-proposed-${template.id}`,
            });
          }
        });
        if (generatorOpts.length > 0) {
          opts.push({ groupTitle: t("Generator PDF") });
          opts.push(...generatorOpts);
        }
      }

      return opts;
    }, [
      hasSelectedCalculations,
      hasContractingTemplates,
      hasSexyQuotePdfTemplates,
      hasPdgGeneratorTemplates,
      contractingPdfs,
      sexyQuotePdfTemplates,
      pdfGeneratorTemplates,
      calculationType,
      t,
    ]);

  const handleChange = (
    newValue: PdfDownloadArgs,
    _event: SelectChangeEvent<PdfDownloadArgs | "">,
    _child: React.ReactNode
  ) => {
    onChange(newValue);
  };

  return (
    <Select<PdfDownloadArgs>
      label={label}
      value={
        (options
          .flatMap(o => ("value" in o ? [o.value] : []))
          .find(v => isEqual(v, value)) as PdfDownloadArgs | undefined) ?? null
      }
      onChange={handleChange}
      options={options}
      fullWidth={fullWidth}
      size={size}
      clearable={false}
      loading={loading}
    />
  );
};
